import { Title as MantineTitle, TitleProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedTitleProps extends TitleProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  animate?: boolean;
  /** Polymorphic component to render as (e.g., 'a', 'span', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Title = forwardRef<HTMLHeadingElement, ThemedTitleProps>(
  ({ gradient = false, gradientVariant = 'primary', animate = false, children, style, ...rest }, ref) => {
    const theme = useMantineTheme();

    if (!gradient) {
      return (
        <MantineTitle ref={ref} style={{ textAlign: 'left', ...style }} {...rest}>
          {children}
        </MantineTitle>
      );
    }

    const gradientConfig = theme.other.gradients[gradientVariant];

    return (
      <MantineTitle
        ref={ref}
        style={{
          textAlign: 'left',
          backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundSize: animate ? '200% auto' : '100%',
          animation: animate ? 'gradient-shift 3s ease infinite' : undefined,
          ...style,
        }}
        {...rest}
      >
        {children}
        {animate && (
          <style>{`
            @keyframes gradient-shift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
        )}
      </MantineTitle>
    );
  }
);

Title.displayName = 'Title';

export default Title;
